import { httpsCallable } from "firebase/functions";
import { collection, getDocs, query, where } from "firebase/firestore";
import db, { functions } from "../Firebase.js";

export interface VimplayGameOption {
    id: number;
    name: string;
    type: string;
}

export interface VimplayFreeBetScale {
    id: number;
    name: string;
    minBet: number;
    maxBet: number;
    step: number;
    buttons?: number[];
}

export interface VimplayPlayerBonus {
    uniqueCode: string;
    gameId: number;
    gameName: string;
    freeBetCount: number;
    freeBetAmount: number;
    freeBetRemaining: number;
    expiredAt: number | null;
    minRate: number | null;
    status: string;
    currency: string;
    createdAtMs: number;
}

export interface CreateVimplayBonusInput {
    playerId: string;
    gameId: number;
    freeBetCount: number;
    freeBetAmount: number;
    currency?: string;
    expiredAt?: number;
    minRate?: number;
}

function millis(value: unknown): number {
    if (value && typeof value === "object" && "toMillis" in value &&
        typeof (value as { toMillis: () => number }).toMillis === "function") {
        return (value as { toMillis: () => number }).toMillis();
    }
    return typeof value === "number" ? value : 0;
}

export async function getVimplayGameOptions(): Promise<VimplayGameOption[]> {
    const getGames = httpsCallable(functions, "getVimplayGames");
    const { data } = await getGames();
    const list = Array.isArray(data) ? data : [];
    return list
        .map((game) => {
            const row = game as { id?: number; name?: string; type?: string };
            return {
                id: Number(row.id),
                name: String(row.name ?? ""),
                type: String(row.type ?? ""),
            };
        })
        .filter((game) => Number.isInteger(game.id) && game.id > 0);
}

export async function getVimplayFreeBetScale(gameId: number): Promise<VimplayFreeBetScale | null> {
    const getScale = httpsCallable<
        { currency: string; gameId: number },
        VimplayFreeBetScale[]
    >(functions, "getVimplayFreeBetGames");
    const { data } = await getScale({ currency: "USD", gameId });
    return (data ?? []).find((scale) => scale.id === gameId) ?? data?.[0] ?? null;
}

export async function createVimplayPlayerBonus(
    input: CreateVimplayBonusInput,
): Promise<VimplayPlayerBonus> {
    const create = httpsCallable<CreateVimplayBonusInput, VimplayPlayerBonus>(
        functions,
        "createVimplayPlayerBonus",
    );
    const { data } = await create({ ...input, currency: "USD" });
    return data;
}

export async function listVimplayPlayerBonuses(playerId: string): Promise<VimplayPlayerBonus[]> {
    const bonusQuery = query(
        collection(db, "vimplayBonuses"),
        where("playerId", "==", playerId),
    );
    const snap = await getDocs(bonusQuery);
    return snap.docs
        .map((bonusDoc) => {
            const data = bonusDoc.data();
            return {
                uniqueCode: String(data.uniqueCode ?? bonusDoc.id),
                gameId: Number(data.gameId),
                gameName: String(data.gameName ?? ""),
                freeBetCount: Number(data.freeBetCount ?? 0),
                freeBetAmount: Number(data.freeBetAmount ?? 0),
                freeBetRemaining: Number(data.freeBetRemaining ?? 0),
                expiredAt: data.expiredAt == null ? null : Number(data.expiredAt),
                minRate: data.minRate == null ? null : Number(data.minRate),
                status: String(data.status ?? ""),
                currency: String(data.currency ?? "USD"),
                createdAtMs: millis(data.createdAt),
            };
        })
        .sort((a, b) => b.createdAtMs - a.createdAtMs);
}
